import { computed, inject } from 'vue';
import { useStore } from 'vuex';

import { isSuggestedCategory } from '~/constants/conversationModels';

import {
  GetCategories,
  GetCurrentFields,
  GetCurrentPromptCard,
  GetDisplayPromptsInNewTab,
  GetPrompts,
  GetSelectedCategories,
} from '~/constants/getters';
import {
  SetCategories,
  SetCurrentFields,
  SetCurrentPromptCard,
  SetDisplayPromptsInNewTab,
  SetPrompts,
  SetPromptsDefaultState,
  SetSelectedCategories,
} from '~/constants/actions';

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import useAuthoring from '~/composables/useAuthoring';
import useFormFilters from '~/composables/useFormFilters';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');
  const { getProductAndCountryFilters } = useAuthoring();
  const { formFilters } = useFormFilters();

  const prompts = computed(() => store.getters[`quickPromptsStore/${GetPrompts}`]);
  const currentFields = computed(() => store.getters[`quickPromptsStore/${GetCurrentFields}`]);
  const currentPromptCard = computed(() => store.getters[`quickPromptsStore/${GetCurrentPromptCard}`]);
  const displayPromptsInNewTab = computed(() => store.getters[`quickPromptsStore/${GetDisplayPromptsInNewTab}`]);
  const categories = computed(() => store.getters[`quickPromptsStore/${GetCategories}`]);
  const selectedCategories = computed(() => store.getters[`quickPromptsStore/${GetSelectedCategories}`]);
  const setCurrentFields = (data) => store.dispatch(`quickPromptsStore/${SetCurrentFields}`, data);
  const setCurrentPromptCard = (data) => store.dispatch(`quickPromptsStore/${SetCurrentPromptCard}`, data);
  const setDisplayPromptsInNewTab = (data) => store.dispatch(`quickPromptsStore/${SetDisplayPromptsInNewTab}`, data);
  const setPrompts = (data) => store.dispatch(`quickPromptsStore/${SetPrompts}`, data);

  const setInitialCardsState = () => store.dispatch(`quickPromptsStore/${SetPromptsDefaultState}`);

  const setCategories = (data) => {
    store.dispatch(`quickPromptsStore/${SetCategories}`, data);
  };

  const setSelectedCategories = (data) => store.dispatch(`quickPromptsStore/${SetSelectedCategories}`, data);

  const onSelectPrompt = async (prompt, variablesToFill = []) => {
    await getPromptCardByVersionId({ id: prompt.id, variablesToFill });
    setDisplayPromptsInNewTab(true);
  };

  const getPromptCardsByCategory = async ({ categories, isSuggested }) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.quickPrompts.getPromptCardsByCategory, {
        cardGroupIds: categories || [],
        isSuggested,
      });

      setPrompts(data);

      return data;
    } catch (error) {
      console.log('Failed to get cards', error);
    }
  };

  const fillCurrentVariablesWithData = (variables, variablesData) => {
    if (!variablesData || !variablesData.length) {
      return variables;
    }

    const result = variables.map((variable) => {
      const variableToFill = variablesData.find((v) => v.variable === variable.key);
      if (variableToFill) {
        return {
          ...variable,
          value: variableToFill.value,
        };
      }
      return variable;
    });

    return result;
  };

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  const completeFilterValuesForProductAndCountry = async (variables, isInputDisabled = false) => {
    if (!isInputDisabled) {
      const result = variables.map((variable) => {
        if (variable.key.toLowerCase() === 'product') {
          return {
            ...variable,
            value: capitalize(formFilters.value['metadata.product']),
          };
        }
        if (variable.key.toLowerCase() === 'country') {
          return {
            ...variable,
            value: capitalize(formFilters.value['metadata.country']),
          };
        }
        return variable;
      });
      return result;
    }
    return variables;
  };

  const completeOptionsAndFiltersForProductAndCountry = async (variables, isInputDisabled = false) => {
    const productAndCountryFilterValues = await getProductAndCountryFilters();
    let result = variables.map((variable) => {
      if (variable.key.toLowerCase() === 'product') {
        return {
          ...variable,
          options: productAndCountryFilterValues.find((item) => item.id === 'metadata.product')?.values || [],
        };
      }
      if (variable.key.toLowerCase() === 'country') {
        return {
          ...variable,
          options: productAndCountryFilterValues.find((item) => item.id === 'metadata.country')?.values || [],
        };
      }
      return variable;
    });
    result = await completeFilterValuesForProductAndCountry(result, isInputDisabled);
    return result;
  };

  const getPromptCardByVersionId = async ({ id, variablesToFill = [], isInputDisabled }) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.quickPrompts.getPromptCardByVersionId(id));

      let variables = data.data;

      if (variablesToFill || currentFields.value.length) {
        variables = fillCurrentVariablesWithData(variables, [...variablesToFill, ...currentFields.value]);
      }

      if (variables.find((item) => item.key.toLowerCase() === 'product' || item.key.toLowerCase() === 'country')) {
        variables = await completeOptionsAndFiltersForProductAndCountry(variables, isInputDisabled);
      }

      //TODO: remove this later and handle label as placeholder in tiptap
      variables = variables.map((variable) => ({
        ...variable,
        placeholder: variable.placeholder || variable.label || '',
      }));

      setCurrentFields(variables);

      const promptData = { ...data, data: variables };
      setCurrentPromptCard(promptData);

      return promptData;
    } catch (error) {
      console.log('Failed to get cards by versionId', error);
    }
  };

  const getCategories = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.quickPrompts.getPromptCardsGroups);
      const categoriesData = [isSuggestedCategory, ...data];

      setSelectedCategories([isSuggestedCategory.id]);
      setCategories(categoriesData);

      return categoriesData;
    } catch (error) {
      console.log('Failed to get categories', error);
    }
  };

  return {
    currentFields,
    setCurrentFields,
    currentPromptCard,
    setCurrentPromptCard,
    setInitialCardsState,
    displayPromptsInNewTab,
    setDisplayPromptsInNewTab,
    onSelectPrompt,
    prompts,
    setPrompts,
    categories,
    setCategories,
    getPromptCardsByCategory,
    getPromptCardByVersionId,
    getCategories,
    selectedCategories,
    setSelectedCategories,
    completeFilterValuesForProductAndCountry,
  };
};
